import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Collapse, Container } from "reactstrap";
import SimpleBar from "simplebar-react";
import withRouter from "../../Components/Common/withRouter";
import { withTranslation } from "react-i18next";
import VerticalLayout from "../VerticalLayouts";
import { useNavData } from "Components/Hooks/useNavData";
import logoSm from "../../assets/images/dtsimg/svg/zilter_logo_blue_small.svg";

const TwoColumnLayout: React.FC<any> = (props) => {
  const { logoDarksm, logoLightsm} = props;
  // Initialize state variables for navigation hook
  const [menuState, setMenuState] = useState<Record<string, boolean>>({
    dashboard: false,
    students: false,
    applications: false,
    institutes: false,
    courses: false,
    finances: false,
    sources: false,
    subagents: false,
    channels: false,
    fields: false,
    users: false,
    roles: false,
    teams: false,
    companies: false,
    forms: false,
    products: false,
    templates: false,
    reports: false,
    imports: false,
  });
  const [currentState, setCurrentState] = useState("Dashboard");

  // Helper to update sidebar icons when a dropdown is toggled
  const updateIconSidebar = useCallback((e: any) => {
    if (e && e.target && e.target.getAttribute("sub-items")) {
      const ul = document.getElementById("two-column-menu");
      if (ul) {
        const iconItems = ul.querySelectorAll(".nav-icon.active");
        Array.from(iconItems).forEach((item) => {
          const element = item as HTMLElement;
          element.classList.remove("active");
          const id = element.getAttribute("sub-items");
          const targetEl = document.getElementById(id || "");
          if (targetEl) {
            targetEl.classList.remove("show");
          }
        });
      }
    }
  }, []);

  // Retrieve nav items via the custom hook
  const navData = useNavData(menuState, setMenuState, setCurrentState, updateIconSidebar);

  const [isMenu, setIsMenu] = useState("twocolumn");
  const path = props.router.location.pathname;

  // Helper: activate parent dropdown for a given element
  const activateParentDropdown = useCallback((item: HTMLElement) => {
    item.classList.add("active");
    const parentCollapseDiv = item.closest(".collapse.menu-dropdown");
    if (parentCollapseDiv) {
      parentCollapseDiv.classList.add("show");
      if (parentCollapseDiv.parentElement?.children[0]) {
        (parentCollapseDiv.parentElement.children[0] as HTMLElement).classList.add("active");
        parentCollapseDiv.parentElement.children[0].setAttribute("aria-expanded", "true");
      }
      if (parentCollapseDiv.parentElement?.closest(".collapse.menu-dropdown")) {
        const parentContainer = parentCollapseDiv.parentElement.closest(".collapse");
        if (parentContainer) {
          parentContainer.classList.add("show");
          const parentParentCollapse = parentContainer.previousElementSibling;
          if (parentParentCollapse) {
            parentParentCollapse.classList.add("active");
          }
        }
      }
      // Activate the sidebar icon for the current dropdown id
      activateIconSidebarActive(parentCollapseDiv.getAttribute("id"));
      return false;
    }
    return false;
  }, []);

  const initMenu = useCallback(() => {
    const pathName = (process.env.PUBLIC_URL || "") + path;
    const ul = document.getElementById("navbar-nav");
    if (!ul) return;
    const items = ul.getElementsByTagName("a");
    const itemsArray = Array.from(items) as HTMLAnchorElement[];
    removeActivation(itemsArray);
    const matchingMenuItem = itemsArray.find((x) => x.pathname === pathName);
    if (matchingMenuItem) {
      activateParentDropdown(matchingMenuItem);
    } else {
      // Fallback: derive id from pathName and add a class if needed
      let id = pathName.replace(process.env.PUBLIC_URL || "", "").replace("/", "");
      if (id) document.body.classList.add("twocolumn-panel");
      activateIconSidebarActive(id);
    }
  }, [path, activateParentDropdown]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    initMenu();
  }, [path, initMenu]);

  function activateIconSidebarActive(id: string | null) {
    if (!id) return;
    const menu = document.querySelector(
      `#two-column-menu .simplebar-content-wrapper a[sub-items='${id}'].nav-icon`
    ) as HTMLElement | null;
    if (menu !== null) {
      menu.classList.add("active");
    }
  }

  const removeActivation = (items: HTMLAnchorElement[]) => {
    const activeItems = items.filter((x) => x.classList.contains("active"));
    activeItems.forEach((item) => {
      if (item.classList.contains("menu-link")) {
        item.setAttribute("aria-expanded", "false");
        if (item.nextElementSibling) {
          item.nextElementSibling.classList.remove("show");
        }
      }
      if (item.classList.contains("nav-link")) {
        if (item.nextElementSibling) {
          item.nextElementSibling.classList.remove("show");
        }
        item.setAttribute("aria-expanded", "false");
      }
      item.classList.remove("active");
    });

    const ul = document.getElementById("two-column-menu");
    if (ul) {
      const iconItems = ul.getElementsByTagName("a");
      const itemsArray = Array.from(iconItems);
      const activeIconItems = itemsArray.filter((x) => x.classList.contains("active"));
      activeIconItems.forEach((item: any) => {
        item.classList.remove("active");
        const id = item.getAttribute("sub-items");
        const getId = document.getElementById(id || "");
        if (getId) getId.classList.remove("show");
      });
    }
  };

  // Resize sidebar when window size changes
  const windowResizeHover = () => {
    initMenu();
    const windowSize = document.documentElement.clientWidth;
    if (windowSize < 767) {
      document.documentElement.setAttribute("data-layout", "vertical");
      setIsMenu("vertical");
    } else {
      document.documentElement.setAttribute("data-layout", "twocolumn");
      setIsMenu("twocolumn");
    }
  };

  useEffect(() => {
    if (props.layoutType === "twocolumn") {
      window.addEventListener("resize", windowResizeHover);
      return () => {
        window.removeEventListener("resize", windowResizeHover);
      };
    }
  }, [props.layoutType]);

  return (
    <React.Fragment>
      {isMenu === "twocolumn" ? (
        <div id="scrollbar">
          <Container fluid>
            <div id="two-column-menu">
              <SimpleBar className="twocolumn-iconview">
                <Link to="#" className="logo">
                  <img src={logoDarksm || logoSm}  alt="Logo" height="22" />
                </Link>
                {navData.map((item: any, key: number) => (
                  <React.Fragment key={key}>
                    {item.icon &&
                      (item.subItems ? (
                        <li>
                          <Link
                            onClick={item.click}
                            to="#"
                            sub-items={item.id}
                            className="nav-icon"
                            data-bs-toggle="collapse"
                          >
                            <i className={item.icon}></i>
                          </Link>
                        </li>
                      ) : (
                        <li>
                          <Link
                            onClick={item.click}
                            to={item.link || "/#"}
                            sub-items={item.id}
                            className="nav-icon"
                            data-bs-toggle="collapse"
                          >
                            <i className={item.icon}></i>
                          </Link>
                        </li>
                      ))}
                  </React.Fragment>
                ))}
              </SimpleBar>
            </div>
            <SimpleBar id="navbar-nav" className="navbar-nav">
              {navData.map((item: any, key: number) => (
                <React.Fragment key={key}>
                  {item.subItems ? (
                    <li className="nav-item">
                      <Collapse className="menu-dropdown" isOpen={item.stateVariables} id={item.id}>
                        <ul className="nav nav-sm flex-column test">
                          {item.subItems.map((subItem: any, key: number) => (
                            <React.Fragment key={key}>
                              {!subItem.isChildItem ? (
                                <li className="nav-item">
                                  <Link to={subItem.link || "/#"} className="nav-link">
                                    {props.t(subItem.label)}
                                    {subItem.badgeName && (
                                      <span className={"badge badge-pill bg-" + subItem.badgeColor} data-key="t-new">
                                        {subItem.badgeName}
                                      </span>
                                    )}
                                  </Link>
                                </li>
                              ) : (
                                <li className="nav-item">
                                  <Link
                                    onClick={subItem.click}
                                    className="nav-link"
                                    to="/#"
                                    data-bs-toggle="collapse"
                                  >
                                    {props.t(subItem.label)}
                                    {subItem.badgeName && (
                                      <span className={"badge badge-pill bg-" + subItem.badgeColor} data-key="t-new">
                                        {subItem.badgeName}
                                      </span>
                                    )}
                                  </Link>
                                  <Collapse className="menu-dropdown" isOpen={subItem.stateVariables} id="sidebarEcommerce">
                                    <ul className="nav nav-sm flex-column">
                                      {subItem.childItems &&
                                        subItem.childItems.map((childItem: any, key: number) => (
                                          <li className="nav-item" key={key}>
                                            <Link
                                              to={childItem.link || "/#"}
                                              className="nav-link"
                                              data-key="t-basic-action"
                                            >
                                              {props.t(childItem.label)}
                                            </Link>
                                          </li>
                                        ))}
                                    </ul>
                                  </Collapse>
                                </li>
                              )}
                            </React.Fragment>
                          ))}
                        </ul>
                      </Collapse>
                    </li>
                  ) : null}
                </React.Fragment>
              ))}
            </SimpleBar>
          </Container>
        </div>
      ) : (
        <SimpleBar id="scrollbar" className="h-100">
          <Container fluid>
            <div id="two-column-menu"></div>
            <ul className="navbar-nav" id="navbar-nav">
              <VerticalLayout {...props} />
            </ul>
          </Container>
        </SimpleBar>
      )}
    </React.Fragment>
  );
};

TwoColumnLayout.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};

export default withRouter(withTranslation()(TwoColumnLayout));
